import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { Button, PageHeader, Pagination } from '@/shared/components';
import type { UserResponse } from '@/shared/api';
import { useAuth } from '@/shared/auth';
import { UsersBulkBar } from './bulk/UsersBulkBar';
import { UsersBulkResultDialog } from './bulk/UsersBulkResultDialog';
import { useBulkUserActions } from './bulk/useBulkUserActions';
import { UsersEmptyState } from './UsersEmptyState';
import { UsersFilterBar } from './UsersFilterBar';
import {
  CLOSED_LIST_DIALOGS,
  UsersPageDialogs,
  type UsersListDialogState,
} from './UsersPageDialogs';
import { UsersTable } from './UsersTable';
import { WorkspaceTwoFactorPolicyCard } from './WorkspaceTwoFactorPolicyCard';
import { fullName } from './userStatus';
import { useUserMutations } from './useUserMutations';
import { EMPTY_USER_FILTERS } from './usersListSearch';
import { USERS_PAGE_SIZE, useUsersList } from './useUsersList';

export function UsersPage() {
  const navigate = useNavigate();
  const currentUserId = useAuth().user?.userId;
  const list = useUsersList();

  const [dialogs, setDialogs] = useState<UsersListDialogState>(CLOSED_LIST_DIALOGS);
  const patch = (next: Partial<UsersListDialogState>) =>
    setDialogs((current) => ({ ...current, ...next }));

  const mutations = useUserMutations({
    onDeactivated: () => patch({ deactivateTarget: null }),
    onRenamed: () => patch({ nameTarget: null }),
    onAccessUpdated: () => undefined,
    onTwoFactorReset: () => patch({ resetTarget: null }),
  });
  const bulk = useBulkUserActions(currentUserId);

  // Ladezustand an der Zeile statt an der Mutation: sonst dreht ein Klick jeden Knopf der Seite
  // — derselbe Befund, den Block D in fünf Listen hatte.
  const rowBusyId = mutations.resend.isPending
    ? (mutations.resend.variables ?? null)
    : mutations.reactivate.isPending
      ? (mutations.reactivate.variables ?? null)
      : null;

  return (
    <>
      <PageHeader
        eyebrow="Verwaltung"
        title="Ihr"
        titleAccent="Team"
        description={
          list.hasData
            ? `${list.total} Benutzer in diesem Workspace${list.filtered ? ' — gefiltert' : ''}.`
            : 'Wird geladen…'
        }
        actions={
          <Button
            variant="brand"
            leftIcon={<Plus size={14} aria-hidden />}
            onClick={() => patch({ create: true })}
          >
            Benutzer einladen
          </Button>
        }
      />

      <WorkspaceTwoFactorPolicyCard />

      <UsersFilterBar value={list.filters} onChange={list.setFilters} />

      <UsersBulkBar
        selected={list.selected}
        currentUserId={currentUserId}
        running={bulk.running}
        onRun={(action) => void bulk.run(action, list.selected).then(list.clearSelection)}
        onClear={list.clearSelection}
      />

      {list.emptyKind ? (
        <UsersEmptyState
          kind={list.emptyKind}
          onResetFilters={() => list.setFilters(EMPTY_USER_FILTERS)}
          onInvite={() => patch({ create: true })}
        />
      ) : (
        <>
          <UsersTable
            users={list.items}
            selectedIds={list.selectedIds}
            onToggle={list.toggle}
            onToggleAll={list.toggleAll}
            currentUserId={currentUserId}
            busyId={rowBusyId}
            onRename={(user: UserResponse) => patch({ nameTarget: user })}
            onChangeRole={(user: UserResponse) =>
              void navigate({ to: '/users/$id', params: { id: user.id } })
            }
            onAssign={(user: UserResponse) =>
              patch({ assignSubject: { kind: 'user', id: user.id, label: fullName(user) } })
            }
            onGenerateInvitation={(user: UserResponse) => patch({ invitationTarget: user })}
            onResend={(id: string) => mutations.resend.mutate(id)}
            onDeactivate={(id: string) =>
              patch({ deactivateTarget: list.items.find((u) => u.id === id) ?? null })
            }
            onReactivate={(id: string) => mutations.reactivate.mutate(id)}
            onResetTwoFactor={(user: UserResponse) => patch({ resetTarget: user })}
          />

          {list.hasData && (
            <Pagination
              page={list.page}
              pageSize={USERS_PAGE_SIZE}
              total={list.total}
              onPageChange={list.setPage}
            />
          )}
        </>
      )}

      <UsersPageDialogs state={dialogs} onChange={patch} mutations={mutations} />

      <UsersBulkResultDialog
        action={bulk.result?.action ?? null}
        outcome={bulk.result?.outcome ?? null}
        onClose={bulk.dismissResult}
      />
    </>
  );
}
